import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { 
  Settings, 
  Building2, 
  Users, 
  Shield, 
  Plus,
  User,
  Mail
} from "lucide-react";
import { toast } from "sonner";

const roleOptions = [
  { value: "admin", label: "Administrator", className: "bg-primary/20 text-primary" },
  { value: "fleet_manager", label: "Fleet Manager", className: "bg-blue-500/20 text-blue-400" },
  { value: "driver", label: "Driver", className: "bg-yellow-500/20 text-yellow-400" }
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  
  const [orgFormData, setOrgFormData] = useState({
    name: "",
    code: "",
    address: "",
    contact_phone: ""
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [orgsRes] = await Promise.all([
        apiClient.get("/organizations")
      ]);
      setOrganizations(orgsRes.data);
      
      if (isAdmin) {
        const usersRes = await apiClient.get("/users");
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/organizations", orgFormData);
      toast.success("Organization created successfully");
      setOrgDialogOpen(false);
      setOrgFormData({ name: "", code: "", address: "", contact_phone: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organization");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiClient.put(`/users/${userId}/role?role=${newRole}`);
      toast.success("User role updated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const getRoleBadge = (role) => {
    const config = roleOptions.find(r => r.value === role);
    return config || roleOptions[2];
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
          SYSTEM SETTINGS
        </h1>
        <p className="text-sm font-mono text-muted-foreground mt-1">
          Configure organizations and user permissions
        </p>
      </div>

      {/* Current User Info */}
      <Card className="bg-card border-border rounded-sm" data-testid="current-user-card">
        <CardHeader className="border-b border-border pb-4">
          <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            CURRENT USER
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-xl font-heading font-bold text-foreground">{user?.full_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-3 h-3 text-muted-foreground" />
                  <span className="text-sm font-mono text-muted-foreground">{user?.email}</span>
                </div>
              </div>
              <Badge className={`${getRoleBadge(user?.role).className} rounded-sm font-mono text-xs`}>
                {getRoleBadge(user?.role).label}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organizations */}
        <Card className="bg-card border-border rounded-sm" data-testid="organizations-card">
          <CardHeader className="border-b border-border pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary" />
                  ORGANIZATIONS
                </CardTitle>
                <CardDescription className="text-xs font-mono text-muted-foreground mt-1">
                  Manage fleet organizations
                </CardDescription>
              </div>
              {isAdmin && (
                <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-mono text-xs" data-testid="add-org-btn">
                      <Plus className="w-3 h-3 mr-1" />
                      ADD
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border rounded-sm max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-lg tracking-wide">
                        CREATE ORGANIZATION
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateOrg} className="space-y-4" data-testid="org-form">
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Name</Label>
                        <Input
                          value={orgFormData.name}
                          onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                          placeholder="Organization name"
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          required
                          data-testid="org-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Code</Label>
                        <Input
                          value={orgFormData.code}
                          onChange={(e) => setOrgFormData({ ...orgFormData, code: e.target.value.toUpperCase() })}
                          placeholder="ORG"
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          required
                          data-testid="org-code-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Address</Label>
                        <Input
                          value={orgFormData.address}
                          onChange={(e) => setOrgFormData({ ...orgFormData, address: e.target.value })}
                          placeholder="Organization address"
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          data-testid="org-address-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-mono text-muted-foreground uppercase">Contact Phone</Label>
                        <Input
                          value={orgFormData.contact_phone}
                          onChange={(e) => setOrgFormData({ ...orgFormData, contact_phone: e.target.value })}
                          placeholder="+255 22 123 4567"
                          className="bg-input border-border rounded-sm font-mono text-sm"
                          data-testid="org-phone-input"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="org-submit-btn">
                        CREATE ORGANIZATION
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {organizations.map((org) => (
                <div key={org.id} className="p-4" data-testid={`org-item-${org.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-foreground">{org.name}</p>
                      <p className="text-xs font-mono text-primary mt-0.5">{org.code}</p>
                    </div>
                    <Badge className="bg-accent text-accent-foreground rounded-sm font-mono text-[10px]">
                      ORG
                    </Badge>
                  </div>
                  {org.address && (
                    <p className="text-xs text-muted-foreground mt-2">{org.address}</p>
                  )}
                  {org.contact_phone && (
                    <p className="text-xs font-mono text-muted-foreground mt-1">{org.contact_phone}</p>
                  )}
                </div>
              ))}
              {organizations.length === 0 && (
                <div className="p-8 text-center text-muted-foreground font-mono text-sm">
                  No organizations found
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role Permissions Info */}
        <Card className="bg-card border-border rounded-sm" data-testid="roles-info-card">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              ROLE PERMISSIONS
            </CardTitle>
            <CardDescription className="text-xs font-mono text-muted-foreground mt-1">
              Access levels by role
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-primary/20 text-primary rounded-sm font-mono text-xs">
                    Administrator
                  </Badge>
                </div>
                <ul className="text-xs font-mono text-muted-foreground space-y-1">
                  <li>• Full system access</li>
                  <li>• Manage organizations</li>
                  <li>• Manage users and roles</li>
                  <li>• Delete any records</li>
                </ul>
              </div>
              
              <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-blue-500/20 text-blue-400 rounded-sm font-mono text-xs">
                    Fleet Manager
                  </Badge>
                </div>
                <ul className="text-xs font-mono text-muted-foreground space-y-1">
                  <li>• Manage vehicles</li>
                  <li>• Manage drivers</li>
                  <li>• Add service & fuel records</li>
                  <li>• View analytics</li>
                </ul>
              </div>
              
              <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-yellow-500/20 text-yellow-400 rounded-sm font-mono text-xs">
                    Driver
                  </Badge>
                </div>
                <ul className="text-xs font-mono text-muted-foreground space-y-1">
                  <li>• View assigned vehicles</li>
                  <li>• Add fuel records</li>
                  <li>• View own data</li>
                  <li>• Limited dashboard access</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Management (Admin only) */}
      {isAdmin && (
        <Card className="bg-card border-border rounded-sm" data-testid="user-management-card">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              USER MANAGEMENT
            </CardTitle>
            <CardDescription className="text-xs font-mono text-muted-foreground mt-1">
              Manage system users and their roles
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">USER</TableHead>
                    <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">EMAIL</TableHead>
                    <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">ROLE</TableHead>
                    <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">JOINED</TableHead>
                    <TableHead className="font-heading text-xs tracking-wide text-muted-foreground text-right">ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const roleBadge = getRoleBadge(u.role);
                    return (
                      <TableRow key={u.id} className="border-border hover:bg-accent/50" data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
                              <User className="w-4 h-4 text-primary" />
                            </div>
                            <span className="font-medium text-foreground">{u.full_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-muted-foreground">
                          {u.email}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${roleBadge.className} rounded-sm font-mono text-xs`}>
                            {roleBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {u.id !== user?.id && (
                            <Select 
                              value={u.role} 
                              onValueChange={(value) => handleRoleChange(u.id, value)}
                            >
                              <SelectTrigger className="w-36 bg-input border-border rounded-sm font-mono text-xs h-8" data-testid={`user-role-select-${u.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border rounded-sm">
                                {roleOptions.map(role => (
                                  <SelectItem key={role.value} value={role.value} className="font-mono text-xs">
                                    {role.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground font-mono">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
